import { useCallback, useEffect, useRef } from 'react';
import useInputComposer, { DEFAULT_INPUT_COMPOSER_UPLOAD_LIMITS } from '$/hooks/useInputComposer';
import type { InputComposerTrackLocation } from '$/hooks/useInputComposer/types';
import { useCommonStore } from '@/store/useCommonStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import { trackRoboneoAssetUploadClick } from '@/components/AssetModalContent/tracking';

type UseHomeInputPanelComposerParams = {
  trackLocation?: InputComposerTrackLocation;
  enableSelectMode?: boolean;
  enableThinkingMode?: boolean;
  enableSkillSelect?: boolean;
  forceShowModelSelect?: boolean;
};

/** 创建并初始化首页新版输入面板 composer，复用登录和资产库前置逻辑。 */
export default function useHomeInputPanelComposer({
  trackLocation = 'home_page',
  enableSelectMode = true,
  enableThinkingMode = true,
  enableSkillSelect = true,
  forceShowModelSelect = true,
}: UseHomeInputPanelComposerParams = {}) {
  const initialized = useCommonStore((state) => state.initialized);
  const initUserInfo = useCommonStore((state) => state.initUserInfo);
  const isUserLoggedIn = useOfficialStore((state) => state.isUserLoggedIn());
  const toLogin = useOfficialStore((state) => state.toLogin);
  const initializedComposerRef = useRef(false);

  /** 打开资产库前确认登录态，未登录时等待登录完成。 */
  const beforeOpenAssetLibrary = useCallback(async () => {
    if (!isUserLoggedIn) {
      await toLogin().then(() => { initUserInfo(); }).catch(() => {});
      return false;
    }
    return true;
  }, [initUserInfo, isUserLoggedIn, toLogin]);

  /** 统一记录首页输入面板资产库入口点击。 */
  const handleAssetLibraryOpen = useCallback(() => {
    trackRoboneoAssetUploadClick('home_page');
  }, []);

  const inputComposer = useInputComposer({
    surface: 'home',
    config: {
      ...DEFAULT_INPUT_COMPOSER_UPLOAD_LIMITS,
      enableSelectMode,
      enableThinkingMode,
      enableSkillSelect,
      forceShowModelSelect,
      trackLocation,
    },
    beforeOpenAssetLibrary,
    onAssetLibraryOpen: handleAssetLibraryOpen,
  });

  /** 初始化输入工具、模型和 Skill 数据，只在全局初始化完成后执行一次。 */
  useEffect(() => {
    if (!initialized || initializedComposerRef.current) return;
    initializedComposerRef.current = true;
    inputComposer.actions.initInputButtonConfig();
    inputComposer.actions.initModelConfig();
    if (enableSkillSelect) {
      inputComposer.actions.initSkillData();
    }
  }, [enableSkillSelect, initialized, inputComposer.actions]);

  return inputComposer;
}
